import type { PitchFrame } from '@tunasong/music-lib'
import type { AttemptTimeline } from './trainer-timeline.js'

/**
 * One attempt: open the input, listen to the room, cue, play the melody, hand over the pulse, take.
 *
 * Every side effect is injected, so the three orderings a broken trainer would break SILENTLY are
 * unit-testable without an audio graph. Each of them is a rule about what must not sound, or must
 * not be listening, at a particular moment:
 *
 *  1. **The microphone opens before anything sounds.** The voicing gate measures the noise floor
 *     from the first 500 ms of the window (vocal spec §5.1). In this tool the count-in and the
 *     reference reach the player through headphones, so the whole pre-take stretch is genuine room
 *     tone - but only if the frames were already arriving. Start the input after the cue and the
 *     floor is measured from whatever the room did next.
 *  2. **Count-in, then reference, then the click.** Anything pitched between what the player heard
 *     and what they are about to reproduce erodes pitch memory - the lesson the Pitch Trainer
 *     learned from a real user in September. The knocks are unpitched and PRECEDE the reference;
 *     the click after the silent beat is the same woodblock, so the pulse is one continuous voice
 *     rather than a second cue.
 *  3. **The reference is stopped before the take - at Level 2.** A soundfont release tail ringing
 *     under bar 1 would be segmented and scored as the player's first note. In audio modes
 *     headphones also keep it off the microphone, but the tool must not depend on the user's
 *     hardware to be correct.
 *
 * `referenceDuringTake` (§12.1's Level 1) suspends rules 2 and 3 in the one way the level defines:
 * the melody sounds THROUGH the take instead of in front of it, so there is no listen phase and no
 * silent beat, and the stop moves to the end. Rule 1 is untouched, and so is everything about
 * scoring - what changes is what the singer can hear while they sing, and nothing else. The option
 * is optional and defaults to false, which is the sequence every caller had before it existed.
 *
 * `wait` is injected for a second reason beyond testing: the host's implementation resolves early
 * when the user presses Stop, which is how an attempt ends before its last bar without this file
 * knowing anything about buttons.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.4
 */

export type AttemptPhase = 'lead-in' | 'count-in' | 'reference' | 'gap' | 'take' | 'scoring'

export interface AttemptDeps {
  /** Audio modes only. Resolves to an unsubscribe. Absent for MIDI, which opens no microphone. */
  startFrames?: (onFrame: (frame: PitchFrame) => void) => Promise<() => void>
  /** Two woodblock knocks at the melody's tempo. Fire-and-forget; the waits own the clock. */
  playCountIn: () => void
  /** The melody with its feel. Fire-and-forget. */
  playReference: () => void
  /** Silence the reference, tail included. */
  stopReference: () => void
  /** The take's click, starting ON beat 1 - it carries no count-in of its own. */
  playClick: () => void
  wait: (ms: number) => Promise<void>
  /** Wall clock. The MIDI events the take collects are timestamped on it. */
  now: () => number
  onPhase?: (phase: AttemptPhase) => void
  /** Every frame as it arrives, re-based, for the live meter - the same frames the attempt keeps. */
  onFrame?: (frame: PitchFrame) => void
}

export interface AttemptResult {
  frames: PitchFrame[]
  /** The take's beat 1, in ms on the frames' re-based clock. */
  t0Ms: number
  /** The take's beat 1, on `now()`'s clock. What the MIDI collector needs. */
  t0WallMs: number
}

export const runTrainerAttempt = async (
  deps: AttemptDeps,
  timeline: AttemptTimeline & { leadInMs: number; referenceDuringTake?: boolean }
): Promise<AttemptResult> => {
  const { startFrames, playCountIn, playReference, stopReference, playClick, wait, now, onPhase, onFrame } = deps
  const { leadInMs, countInMs, referenceMs, gapMs, takeMs, tailMs, referenceDuringTake = false } = timeline

  const frames: PitchFrame[] = []
  /**
   * The worklet timestamps frames from its own construction, not from this attempt. On the fifth
   * attempt of a session the first frame arrives tens of seconds in, and both `noiseFloorRms`
   * (which selects `time < 0.5`) and beat 1 would land in the wrong place.
   */
  let origin: number | null = null
  let latestMs = 0

  const stopFrames = await startFrames?.(frame => {
    origin = origin ?? frame.time
    const rebased = { ...frame, time: frame.time - origin }
    latestMs = rebased.time * 1000
    frames.push(rebased)
    onFrame?.(rebased)
  })

  let t0Ms = 0
  let t0WallMs = 0
  try {
    onPhase?.('lead-in')
    await wait(leadInMs)

    onPhase?.('count-in')
    playCountIn()
    await wait(countInMs)

    /*
     * Sing back (§6.4): the melody is heard, then silenced, then reproduced from memory. The
     * silent beat and the stop are what make it a memory exercise at all.
     *
     * Sing along (§12.1): the melody sounds THROUGH the take. There is no listen phase in front of
     * it and no silent beat to separate them, because the singer is in unison with it - that is
     * the whole difference between the two levels. Everything else, the room tone and the knocks
     * included, is unchanged.
     */
    if (!referenceDuringTake) {
      onPhase?.('reference')
      playReference()
      await wait(referenceMs)

      /* Stopped at the top of the silent beat, so the beat is silent all the way through. */
      stopReference()
      onPhase?.('gap')
      await wait(gapMs)
    }

    /*
     * The frame clock and the wall clock are both read HERE, at the instant the click starts,
     * rather than derived from the waits: the two clocks drift, and an onset measured against a
     * beat 1 that was assumed rather than observed inherits the drift as a timing error.
     */
    t0Ms = latestMs
    t0WallMs = now()
    onPhase?.('take')
    playClick()
    if (referenceDuringTake) {
      /* After beat 1 is stamped, so the reference cannot displace the clock it is measured against. */
      playReference()
    }
    await wait(takeMs + tailMs)
  } finally {
    /*
     * Sing along leaves the melody sounding into the tail, so its stop belongs here: `finally` also
     * covers the throw path, where a reference left ringing would play under the NEXT attempt's
     * room tone and raise the noise floor the voicing gate is measured against.
     */
    if (referenceDuringTake) {
      stopReference()
    }
    stopFrames?.()
  }

  onPhase?.('scoring')
  return { frames, t0Ms, t0WallMs }
}
